
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { industries } from '@/types/survey';
import Logo from '@/components/Logo';
import { useLanguage } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useAuth } from '@/contexts/AuthContext';
import { UserIcon, AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';

const IndustrySelector = () => {
  const [selectedIndustry, setSelectedIndustry] = useState<string>('');
  const [projectName, setProjectName] = useState<string>('');
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    const savedIndustry = sessionStorage.getItem('nev-industry');
    const savedProjectName = sessionStorage.getItem('nev-project-name');
    if (savedIndustry) setSelectedIndustry(savedIndustry);
    if (savedProjectName) setProjectName(savedProjectName);
  }, []);

  const handleContinue = () => {
    if (!selectedIndustry) {
      toast({
        title: t('error.required'),
        description: t('industry.selectRequired'),
        variant: "destructive",
      });
      return;
    }

    if (!projectName.trim()) {
      toast({
        title: t('error.required'),
        description: t('industry.nameRequired'),
        variant: "destructive",
      });
      return;
    }

    sessionStorage.setItem('nev-industry', selectedIndustry);
    sessionStorage.setItem('nev-project-name', projectName.trim());
    
    navigate('/survey');
  };

  const handleRegisterAndStart = () => {
    if (!selectedIndustry) {
      toast({
        title: t('error.required'),
        description: t('industry.selectRequired'),
        variant: "destructive",
      });
      return;
    }

    if (!projectName.trim()) {
      toast({
        title: t('error.required'),
        description: t('industry.nameRequired'),
        variant: "destructive",
      });
      return;
    }

    sessionStorage.setItem('nev-industry', selectedIndustry);
    sessionStorage.setItem('nev-project-name', projectName.trim());
    
    navigate('/auth', { 
      state: { 
        from: { pathname: '/survey' },
        registrationFlow: true,
        projectName: projectName.trim(),
        industry: selectedIndustry
      } 
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div className="absolute top-16 left-1/2 transform -translate-x-1/2 w-full max-w-md bg-white rounded-lg shadow-lg opacity-70" 
           style={{ height: '200px', zIndex: 0 }}>
      </div>
      
      <div className="w-full max-w-md relative z-10">
        <div className="text-center mb-8">
          <div className="flex justify-between mb-4 items-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-sm"
            >
              ← Back to Home
            </Button>
            <LanguageSwitcher />
          </div>
          
          <Logo className="mx-auto mb-4" size="lg" showVersion={true} />
          
          <div className="mb-2 flex items-center justify-center">
            <div className="h-8 w-8 flex items-center justify-center rounded-full mr-2 bg-primary text-white">
              1
            </div>
            <div className="text-sm font-medium text-muted-foreground">{t('step.1')} {t('nav.of')} 3</div>
          </div>
          
          <h1 className="text-3xl font-bold mb-2">{t('industry.pageTitle')}</h1>
          <p className="text-gray-500">{t('industry.pageSubtitle')}</p>
        </div>

        {!user && (
          <Alert variant="destructive" className="mb-4 bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-600">
              You're not signed in. Your results won't be saved to your account.{" "}
              <Button 
                variant="link" 
                className="h-auto p-0 text-amber-700 underline"
                onClick={() => navigate('/auth', { state: { from: { pathname: "/industry" } } })}
              >
                Sign in now
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{t('industry.detailsTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="projectName">{t('industry.projectName')}</Label>
              <Input 
                id="projectName" 
                value={projectName} 
                onChange={(e) => setProjectName(e.target.value)}
                placeholder={t('industry.projectNamePlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="industry">{t('industry.title')}</Label>
              <div className="grid grid-cols-2 gap-2">
                {industries.map((industry) => {
                  let industryKey = industry.toLowerCase();
                  industryKey = industryKey.replace(/[&.]/g, '').replace(/\s+/g, '_');
                  
                  return (
                    <Button
                      key={industry}
                      type="button"
                      variant={selectedIndustry === industry ? "default" : "outline"}
                      className="h-auto py-2 justify-start"
                      onClick={() => setSelectedIndustry(industry)}
                    >
                      {industry === 'Web 3.0' ? 'Web 3.0' : t(`industry.${industryKey}`)}
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            {!user ? (
              <>
                <Button 
                  onClick={handleRegisterAndStart} 
                  disabled={!selectedIndustry || !projectName.trim()}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  Register and Start Assessment
                </Button>
                <p className="text-xs text-center text-gray-500">Or</p>
                <Button 
                  onClick={handleContinue} 
                  disabled={!selectedIndustry || !projectName.trim()}
                  variant="outline"
                  className="w-full"
                >
                  Continue as Guest
                </Button>
              </>
            ) : (
              <Button 
                onClick={handleContinue} 
                disabled={!selectedIndustry || !projectName.trim()}
                className="w-full"
              >
                {t('industry.continue')}
              </Button>
            )}
            
            {!user && (
              <div className="text-center text-sm">
                <Button 
                  variant="link" 
                  onClick={() => navigate('/auth', { state: { from: { pathname: "/industry" } } })}
                  className="flex items-center justify-center gap-2"
                >
                  <UserIcon className="h-4 w-4" />
                  Sign in to save your results
                </Button>
              </div>
            )}
          </CardFooter>
        </Card>
        
        <p className="mt-6 text-center text-sm text-gray-500">
          {t('industry.assessmentInfo')}
        </p>
      </div>
    </div>
  );
};

export default IndustrySelector;
